import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { CSkeleton, Poppins } from 'src/common';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { ValidationErrorItem, ControlCSV, ParseResult } from './types';
import { generateCsvTemplateLink, getFrameworkControlIds, pathNames } from './util';
import { selectUserOption, pluralize } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import CSelect from 'src/components/form/Select';
import { controllStatusOptions, strategyOptions } from 'src/api/types';
import { ControlCsvHeaders } from 'src/api/types/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import {
  ControlInput,
  MaturityLevel,
  useControlFrameworksListQuery,
  useControlsQuery,
  useUpsertControlsMutation,
} from 'src/graphql/_generated';
import {
  convertDateToISO,
  convertToNumber,
  convertToString,
  convertToMaturityLevel,
  delimiters,
  validateRow,
} from 'src/utils/csv';
import { isQueryLoading, useCache } from 'src/graphql';
import { rmpValidationSchema } from 'src/components/formComposed/RmpForm/util';
import {
  CsvDropzoneWithTemplate,
  CsvValidationErrors,
  CsvUploadButtons,
  SCVSummary,
  DzoneWrap,
} from 'src/components/CsvUpload';

const pluralizeControls = (count: number) => pluralize({ count, singular: 'control', plural: 'controls' });

interface FromCsvProps {
  onClose: () => void;
  setIsScvDirty: (isDirty: boolean) => void;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
}

export const FromCsv: React.FC<FromCsvProps> = ({ onClose, setIsScvDirty, state }) => {
  const { usersOptios, workspaceId } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [validControls, setValidControls] = useState<ControlInput[]>([]);
  const { refetchQuery } = useCache();

  const { data: frameworksList, loading: isFrameworksLoading, networkStatus } = useControlFrameworksListQuery();
  const isFrameworksListLoading = isQueryLoading({ loading: isFrameworksLoading, data: frameworksList, networkStatus });

  const { data: { controls: existingControls = [] } = {} } = useControlsQuery();

  const { file, setFile, setReadyForUpload } = state;

  const [upsertControls, { loading }] = useUpsertControlsMutation();

  const parseCSVToJSON = useCallback(
    (file: File) => {
      const tryParse = (delimiter: string) =>
        new Promise<ParseResult<ControlCSV>>((resolve, reject) => {
          Papa.parse(file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            delimiter,
            complete: resolve,
            error: reject,
          });
        });

      (async () => {
        let result: ParseResult<ControlCSV> | null = null;
        for (const delim of delimiters) {
          const parsed = await tryParse(delim);
          if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
            result = parsed;
            break;
          }
        }

        if (!result || !result.data || result.data.length === 0) {
          setValidationErrors([
            { rowIndex: 0, errors: [{ path: 'Data', message: 'No control data found in the file.' }] },
          ]);
          return;
        }

        if (result.data.length > 60) {
          setError('The number of controls exceeds the limit of 60.');
          setFile(null);
          return;
        }

        const mappedControls: ControlInput[] = result.data.map((el) => {
          const frameworkControlIds = getFrameworkControlIds(el, frameworksList);

          return {
            frameworkControlIds,
            ...(el[ControlCsvHeaders.ID] && { id: convertToString(el[ControlCsvHeaders.ID]) }),
            name: el[ControlCsvHeaders.Name] || '',
            ...(el[ControlCsvHeaders.Strategy] && { strategy: convertToString(el[ControlCsvHeaders.Strategy]) }),
            ...(el[ControlCsvHeaders.Status] && { status: convertToString(el[ControlCsvHeaders.Status]) }),
            ...(el[ControlCsvHeaders.Owner] && { owner: { text: el[ControlCsvHeaders.Owner] } }),
            ...(el[ControlCsvHeaders.AnnualCost] && {
              annualCost: convertToNumber(el[ControlCsvHeaders.AnnualCost]),
            }),
            ...(el[ControlCsvHeaders.Vendor] && { vendor: convertToString(el[ControlCsvHeaders.Vendor]) }),
            ...(el[ControlCsvHeaders.ExpectedLiveDate] && {
              expectedActiveDate: convertDateToISO(el[ControlCsvHeaders.ExpectedLiveDate]),
            }),
            ...(el[ControlCsvHeaders.ActualLiveDate] && {
              actualActiveDate: convertDateToISO(el[ControlCsvHeaders.ActualLiveDate]),
            }),
            ...(el[ControlCsvHeaders.ReviewDate] && {
              reviewDate: convertDateToISO(el[ControlCsvHeaders.ReviewDate]),
            }),
            ...(el[ControlCsvHeaders.Description] && {
              description: convertToString(el[ControlCsvHeaders.Description]),
            }),
            ...(el[ControlCsvHeaders.Source] && { linkUrl: convertToString(el[ControlCsvHeaders.Source]) }),
            ...(el[ControlCsvHeaders.UrlName] && { linkText: convertToString(el[ControlCsvHeaders.UrlName]) }),
            ...(el[ControlCsvHeaders.ExistingMaturity] && {
              existingMaturity: convertToMaturityLevel(el[ControlCsvHeaders.ExistingMaturity]) as MaturityLevel,
            }),
            ...(el[ControlCsvHeaders.TargetMaturity] && {
              targetMaturity: convertToMaturityLevel(el[ControlCsvHeaders.TargetMaturity]) as MaturityLevel,
            }),
          };
        });

        const promises = mappedControls.map((row, idx) =>
          validateRow({ row, rowIndex: idx + 1, schema: rmpValidationSchema, pathNames }),
        );
        const results = await Promise.all(promises);
        const errorLog = results.filter((el) => el?.errors?.length) as ValidationErrorItem[];

        if (errorLog.length) {
          setValidationErrors(errorLog);
          setIsUploadSuccessful(false);
          setReadyForUpload(false);
        } else {
          setValidControls(mappedControls);
          setValidationErrors([]);
          setError('');
          setReadyForUpload(true);
        }
      })();
    },
    [frameworksList, setFile, setReadyForUpload],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      mpEvent(MPEvents.FileLoaded, {
        modal: 'Add RMP modal',
        tags: ['RMP'],
      });
      if (acceptedFiles?.length > 0) {
        const selectedFile = acceptedFiles[0];
        setFile(selectedFile);
        setError('');
        setIsUploadSuccessful(false);
        setValidationErrors([]);
        parseCSVToJSON(selectedFile);
        setIsScvDirty(true);
      }
    },
    [parseCSVToJSON, setFile, setIsScvDirty],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const save = async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Add RMP modal',
      tags: ['RMP'],
    });

    setReadyForUpload(false);

    const processedControls = validControls.map((el) => {
      const { userAdd } = selectUserOption({
        usersOptios,
        selectedUser: { text: el.owner?.text || '' },
      });

      return { ...el, owner: userAdd };
    });

    try {
      await upsertControls({ variables: { controlsData: processedControls, workspaceId } });

      queryCache.invalidateQueries();
      refetchQuery({ include: ['controls'] });

      setIsUploadSuccessful(true);
      setIsScvDirty(false);
      setReadyForUpload(false);
    } catch (error) {
      setError(`Error uploading controls: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setReadyForUpload(true);
    }
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setReadyForUpload(false);
  };

  const newCount = validControls.filter((c) => !c.id || !existingControls.some((e) => e.id === c.id)).length;
  const updatedCount = validControls.filter((c) => c.id && existingControls.some((e) => e.id === c.id)).length;

  return (
    <DzoneWrap className="h-padding">
      {isFrameworksListLoading ? (
        <CSkeleton height={167} />
      ) : (
        <CsvDropzoneWithTemplate
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          file={file}
          templateLink={generateCsvTemplateLink()}
          templateFileName="control_template.csv"
          onTemplateClick={() =>
            mpEvent(MPEvents.DownloadCSVTemplate, {
              modal: 'Add RMP modal',
              tags: ['RMP'],
            })
          }
        />
      )}

      <CsvValidationErrors errors={validationErrors} />

      {file && !validationErrors && (
        <>
          {!isUploadSuccessful && (
            <SCVSummary
              items={[
                'File validated',
                `${updatedCount} ${pluralizeControls(updatedCount)} ready to be updated`,
                `${newCount} ${pluralizeControls(newCount)} ready to be created`,
              ]}
            />
          )}
          {isUploadSuccessful && (
            <SCVSummary
              isCompleted
              items={[
                `${updatedCount} existing ${pluralizeControls(updatedCount)} updated`,
                `${newCount} new ${pluralizeControls(newCount)} added`,
                'File upload successful',
              ]}
            />
          )}
        </>
      )}

      <CsvUploadButtons
        isUploadSuccessful={isUploadSuccessful}
        hasFile={!!file}
        isDisabled={!file || !!validationErrors.length || loading}
        onCancel={onClose}
        onUpload={save}
        onSelectFile={open}
        onUploadAnother={resetUpload}
        onDone={onClose}
      />

      {!file && (
        <>
          <Poppins px={14} weight={500}>
            The valid values of Strategy and Status are listed in the drop downs below. Blank is also valid.
          </Poppins>
          <div className="grid-list grid-list--3">
            <CSelect value={null} placeholder="STRATEGY" menuPlacement="top" options={strategyOptions} />
            <CSelect value={null} placeholder="STATUS" menuPlacement="top" options={controllStatusOptions} />
          </div>
        </>
      )}

      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
